import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import classService from '../services/classService';
import type { ClassItem } from '../types';

/**
 * Teacher Assigned Classes Listing Page
 */
export const MyClasses: React.FC = () => {
  const navigate = useNavigate();
  
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const data = await classService.getMyClasses();
        setClasses(data);
      } catch (err: any) {
        setError('Failed to load your assigned classes. Please verify your connection.');
      } finally {
        setLoading(false);
      }
    };

    fetchClasses();
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', height: '50vh', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner"></div>
          <p style={{ marginTop: '16px', color: 'var(--text-muted)', fontSize: '14px' }}>Loading assigned classes...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ animation: 'var(--transition-normal) fadeIn' }}>
      {/* Title Header */}
      <div className="dashboard-title-bar">
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 800, marginBottom: '4px' }}>🏫 My Classes</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
            Select a class below to register marks, view pupil rosters, or manage daily registers.
          </p>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: 'var(--color-danger-light)',
          color: 'var(--color-danger)',
          padding: '16px',
          borderRadius: 'var(--radius-sm)',
          marginBottom: '24px',
          fontWeight: 500,
          border: '1px solid hsl(355, 80%, 90%)'
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Grid List */}
      {classes.length === 0 ? (
        <div className="glass-panel" style={{ padding: '60px', textAlign: 'center', color: 'var(--text-muted)' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>📭</span>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-main)', marginBottom: '8px' }}>No Assigned Classes Found</h3>
          <p style={{ fontSize: '14px' }}>Please contact your school administrator to link classes to your staff account.</p>
        </div>
      ) : (
        <div className="class-grid">
          {classes.map((cls) => (
            <div 
              key={cls.id} 
              className="class-card"
              onClick={() => navigate(`/classes/${cls.id}`)}
            >
              <div className="class-card-header">
                <span className="class-card-title">{cls.class_name}</span>
                <span className={`badge-role ${cls.is_class_teacher ? 'badge-homeroom' : 'badge-subject'}`}>
                  {cls.is_class_teacher ? 'Homeroom Teacher' : 'Subject Teacher'}
                </span>
              </div>
              <div className="class-card-grade">{cls.grade_name}</div>
              
              <div style={{ 
                borderTop: '1px solid var(--border-color)', 
                paddingTop: '16px', 
                marginTop: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '13px',
                color: 'var(--color-primary)',
                fontWeight: 600
              }}>
                <span>Open Class Dashboard</span>
                <span>&rarr;</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyClasses;
